import { api } from '../../lib/api-client'
import { API_PREFIX } from '../../lib/endpoints'
import type {
  AnalyticsPeriodKind,
  AnalyticsSummaryView,
  FleetHomeView,
  HeatmapSegment,
  UtilizationHeatmapView,
} from './api-types'

/**
 * The three paths this module calls, mirrored from two registered controllers:
 * `DashboardsController` (`/dashboards`) and `AnalyticsController` (`/analytics`).
 * Both are in `app.module.ts` and both require `dashboards:read`, so nothing here is
 * inferred from a naming convention.
 */
export const dashboardEndpoints = {
  /** GET ?fleetId → the nine tiles. Takes NO period: the tiles name their own. */
  fleetHome: `${API_PREFIX}/dashboards/fleet`,
  /** GET ?period&from&to&fleetId&includeInactive → segmented figures + comparison. */
  analyticsSummary: `${API_PREFIX}/analytics/summary`,
  /** GET … &segment → utilisation by local hour × weekday. */
  utilization: `${API_PREFIX}/analytics/utilization`,
} as const

function search(params: Record<string, string | number | boolean | undefined>): string {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue
    query.set(key, String(value))
  }
  const encoded = query.toString()
  return encoded.length > 0 ? `?${encoded}` : ''
}

export interface FleetHomeParams {
  readonly fleetId?: string
}

export function fetchFleetHome(params: FleetHomeParams = {}): Promise<FleetHomeView> {
  return api.get<FleetHomeView>(dashboardEndpoints.fleetHome + search({ ...params }))
}

/**
 * `from`/`to` are ISO instants and are ONLY accepted with `period=custom` — the API
 * refuses the combination rather than resolving it, so this module must not send them
 * alongside a named period "just in case". The refusal is deliberate on the server
 * side: either reading of that request is a number nobody asked for.
 */
export interface AnalyticsParams {
  readonly period?: AnalyticsPeriodKind
  readonly from?: string
  readonly to?: string
  readonly fleetId?: string
  readonly includeInactive?: boolean
}

export function fetchAnalyticsSummary(params: AnalyticsParams = {}): Promise<AnalyticsSummaryView> {
  return api.get<AnalyticsSummaryView>(dashboardEndpoints.analyticsSummary + search({ ...params }))
}

export interface UtilizationParams extends AnalyticsParams {
  readonly segment?: HeatmapSegment
}

export function fetchUtilization(params: UtilizationParams = {}): Promise<UtilizationHeatmapView> {
  return api.get<UtilizationHeatmapView>(dashboardEndpoints.utilization + search({ ...params }))
}
